import { createFileRoute } from '@tanstack/react-router';
import { Check, Copy, KeyRound, Loader2, Plus, Trash2 } from 'lucide-react';
import { Fragment, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useAdminPersonalAccessTokens } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AdminPersonalAccessTokenCreateResponse,
    type AdminPersonalAccessTokenRead,
} from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/access-tokens/')({
    head: () => ({
        meta: [
            {
                title: 'Access Tokens | Phoebe Admin',
            },
        ],
    }),
    component: AccessTokensPage,
});

function AccessTokensPage() {
    const {
        data: tokensResponse,
        isLoading,
        mutate: mutateTokens,
    } = useAdminPersonalAccessTokens();
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [createdToken, setCreatedToken] = useState<string | null>(null);
    const [revokeTarget, setRevokeTarget] =
        useState<AdminPersonalAccessTokenRead | null>(null);

    const tokens = tokensResponse?.personal_access_tokens ?? [];

    const nowMs = Date.now();

    const handleCreate = async (name: string) => {
        try {
            const response = (await getApiClient().post({
                url: '/admin/personal-access-tokens',
                body: { name },
            })) as unknown as AdminPersonalAccessTokenCreateResponse;
            setCreatedToken(response.token);
            await mutateTokens();
            toast.success('Token created', { persistNotification: false });
        } catch {
            toast.error('Failed to create token', {
                persistNotification: false,
            });
        }
    };

    const handleRevoke = async (tokenId: string) => {
        setRevokeTarget(null);
        try {
            await getApiClient().post({
                url: '/admin/personal-access-tokens/{token_id}/revoke',
                pathParams: { token_id: tokenId },
                body: {},
            });
            await mutateTokens();
            toast.success('Token revoked', { persistNotification: false });
        } catch {
            toast.error('Failed to revoke token', {
                persistNotification: false,
            });
        }
    };

    return (
        <div className="flex h-full flex-col">
            <div className="border-border border-b p-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Access Tokens</h2>
                    <Button
                        onClick={() => setIsCreateOpen(true)}
                        size="sm"
                        className="gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        Create Token
                    </Button>
                </div>
            </div>
            <ScrollArea className="flex-1">
                {isLoading ? (
                    <div className="flex h-32 items-center justify-center">
                        <div className="text-muted-foreground flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading tokens{'\u2026'}</span>
                        </div>
                    </div>
                ) : tokens.length === 0 ? (
                    <div className="flex h-32 items-center justify-center">
                        <div className="text-muted-foreground text-sm">
                            No access tokens yet
                        </div>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Hint</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Last Used</TableHead>
                                <TableHead>Created</TableHead>
                                <TableHead className="w-[60px]" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {tokens.map((token) => {
                                const isRevoked = token.revoked_at !== null;
                                const isExpired =
                                    token.expires_at !== null &&
                                    new Date(token.expires_at).getTime() <=
                                        nowMs;

                                let statusLabel = 'Inactive';
                                let statusClassName =
                                    'bg-muted text-foreground/80 hover:bg-accent';

                                if (token.is_active) {
                                    statusLabel = 'Active';
                                    statusClassName =
                                        'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-950/50 dark:text-green-300 dark:hover:bg-green-950/50';
                                } else if (isRevoked) {
                                    statusLabel = 'Revoked';
                                    statusClassName =
                                        'bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-950/50 dark:text-red-300 dark:hover:bg-red-950/50';
                                } else if (isExpired) {
                                    statusLabel = 'Expired';
                                    statusClassName =
                                        'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-300 dark:hover:bg-amber-950/50';
                                }

                                return (
                                    <TableRow key={token.id}>
                                        <TableCell className="font-medium">
                                            {token.name}
                                        </TableCell>
                                        <TableCell>
                                            <code className="text-muted-foreground text-xs">
                                                phb_pat{'\u2026'}
                                                {token.token_hint}
                                            </code>
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant="secondary"
                                                className={statusClassName}
                                            >
                                                {statusLabel}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {token.last_used_at
                                                ? new Date(
                                                      token.last_used_at,
                                                  ).toLocaleDateString()
                                                : '\u2014'}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(
                                                token.created_at,
                                            ).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            {token.is_active && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-red-500 hover:text-red-700 dark:hover:text-red-400"
                                                    onClick={() =>
                                                        setRevokeTarget(token)
                                                    }
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </ScrollArea>
            <CreateTokenDialog
                isOpen={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
                onCreate={handleCreate}
            />
            <TokenCreatedDialog
                token={createdToken}
                onClose={() => setCreatedToken(null)}
            />
            <Dialog
                open={revokeTarget !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setRevokeTarget(null);
                    }
                }}
            >
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle>Revoke token</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to revoke{' '}
                            <strong>{revokeTarget?.name}</strong>? This cannot
                            be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setRevokeTarget(null)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                if (revokeTarget) {
                                    handleRevoke(revokeTarget.id);
                                }
                            }}
                        >
                            Revoke
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function CreateTokenDialog({
    isOpen,
    onClose,
    onCreate,
}: {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly onCreate: (name: string) => Promise<void>;
}) {
    const [name, setName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            return;
        }
        setIsSubmitting(true);
        await onCreate(name.trim());
        setIsSubmitting(false);
        setName('');
        onClose();
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    setName('');
                    onClose();
                }
            }}
        >
            <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                    <DialogTitle>Create Access Token</DialogTitle>
                    <DialogDescription>
                        Create a personal access token for API authentication.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="token-name">Name</Label>
                        <Input
                            id="token-name"
                            placeholder="e.g. revenue-cli"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus={true}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setName('');
                                onClose();
                            }}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting || !name.trim()}
                        >
                            {isSubmitting ? (
                                <Fragment>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating{'\u2026'}
                                </Fragment>
                            ) : (
                                'Create'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function TokenCreatedDialog({
    token,
    onClose,
}: {
    readonly token: string | null;
    readonly onClose: () => void;
}) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (!token) {
            return;
        }
        await navigator.clipboard.writeText(token);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Dialog
            open={token !== null}
            onOpenChange={(open) => {
                if (!open) {
                    setCopied(false);
                    onClose();
                }
            }}
        >
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <KeyRound className="h-5 w-5" />
                        Token Created
                    </DialogTitle>
                    <DialogDescription>
                        Copy this token now. You won{'\u2019'}t be able to see
                        it again.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex items-center gap-2">
                    <code className="bg-muted flex-1 rounded-md border p-3 text-sm break-all">
                        {token}
                    </code>
                    <Button
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={handleCopy}
                    >
                        {copied ? (
                            <Check className="h-4 w-4 text-green-600" />
                        ) : (
                            <Copy className="h-4 w-4" />
                        )}
                    </Button>
                </div>
                <DialogFooter>
                    <Button onClick={onClose}>Done</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
